import { FC, PropsWithChildren, useState } from 'react';
import { LogoImage } from 'assets';
import { MobileFooter } from './MobileFooter';
import { Navbar } from 'components/Navbar/Navbar';
import { R } from 'config/app';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import DesktopFooter from './DesktopFooter';
import Loading from './Loading/Loading';
import LoanCalculatorSettings from './LoanCalculatorSettings';
import LogoutButton from '../LogoutButton/LogoutButton';
import NavbarPlain from '../NavbarPlain';
import Sidebar from './Sidebar';
import SidebarSecondary from './SidebarSecondary';
import styles from './LoanApplicationProcessLayout.module.scss';
import useAuth from 'ui/hooks/useAuth';
import useLoanAmountTracker from './useLoanAmountTracker';
import useLoanApplicationProcess from './LoanApplicationProcessProvider/useLoanApplicationProcess';
import useRedirectToDashboard from 'ui/hooks/useRedirectToDashboard';

const LayoutContent: FC<PropsWithChildren> = (props) => {
  const { children } = props;
  const { isLoggedIn, logout } = useAuth();
  const { t } = useTranslation();
  const [isCalculatorOpen, setIsCalculatorOpen] = useState(false);
  const { progress } = useLoanApplicationProcess();
  const { isRedirectingToDashboard } = useRedirectToDashboard();

  useLoanAmountTracker();

  const logoutClicked = () => {
    logout();
  };

  const redirectWhereFinished = { isRedirectionDetermined: true };

  const { pathname } = useLocation();
  const displayPlainView =
    isRedirectingToDashboard ||
    [
      R.FirstLoanCancelled,
      R.FirstLoanRejected,
      R.FirstLoanRejectedTink,
      R.FirstLoanTinkLoading,
    ].includes(pathname);

  const modifyLoanClicked = () => {
    setIsCalculatorOpen((prev) => !prev);
  };

  const loanCalculatorSettingsCloseTriggered = () => {
    setIsCalculatorOpen(false);
  };

  const loanCalculatorSettingsSubmitted = () => {
    setIsCalculatorOpen(false);
  };

  return (
    <div
      className={classNames(
        styles['wrapper'],
        displayPlainView && styles['layout'],
      )}
    >
      {displayPlainView ? (
        <div className={styles['navbar']}>
          <NavbarPlain />
        </div>
      ) : (
        <>
          <div className={styles['navbar']}>
            <Navbar
              progress={progress}
              onArrowDownClick={modifyLoanClicked}
              isDropdownOpen={isCalculatorOpen}
            />
          </div>
          <div className={styles['left-panel']}>
            <Sidebar onModifyAmountClick={modifyLoanClicked} />
          </div>
          <SidebarSecondary
            isCalculatorOpen={isCalculatorOpen}
            onCalculatorSettingsSubmit={loanCalculatorSettingsSubmitted}
          />
        </>
      )}

      <div
        className={classNames(
          styles['body'],
          isCalculatorOpen && styles['body-overlay--opened'],
        )}
      >
        {displayPlainView && (
          <div className={styles['logo']} data-test="css-Logo">
            <LogoImage />
            {isLoggedIn && (
              <LogoutButton onClick={logoutClicked}>{t('logout')}</LogoutButton>
            )}
          </div>
        )}

        <div className={classNames(styles['body-wrapper'])}>
          {redirectWhereFinished.isRedirectionDetermined ? (
            children
          ) : (
            <Loading />
          )}
        </div>

        {displayPlainView && redirectWhereFinished.isRedirectionDetermined && (
          <DesktopFooter />
        )}

        {!displayPlainView && redirectWhereFinished.isRedirectionDetermined && (
          <div className={styles['logout-button-wrapper']}>
            {isLoggedIn && (
              <LogoutButton onClick={logoutClicked}>{t('logout')}</LogoutButton>
            )}
          </div>
        )}

        {redirectWhereFinished.isRedirectionDetermined && (
          <MobileFooter onLogout={logoutClicked} hideLogout={!isLoggedIn} />
        )}

        <div
          className={classNames(
            styles['body-overlay'],
            isCalculatorOpen ? styles['body-overlay--opened'] : undefined,
          )}
        >
          <div className={classNames(styles['body-overlay-content'])}>
            <LoanCalculatorSettings
              onSubmit={loanCalculatorSettingsSubmitted}
            />
          </div>
          <button
            type="button"
            onClick={loanCalculatorSettingsCloseTriggered}
            className={classNames(styles['body-overlay-shadow'])}
          />
        </div>
      </div>
    </div>
  );
};

export default LayoutContent;
